import React, {useEffect, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {TaskSetDrawer} from './TaskSetDrawer';
import {TaskSetListPane, TaskSetRow} from './TaskSetListPane';
import {TaskSetConfigPane, TaskSetNameChip} from './TaskSetConfigPane';

/**
 * Matches the drawer's close button so the two header controls read as a pair.
 */
const BackButton = styled.button`
	border: 0;
	background: transparent;
	padding: 4px;
	cursor: pointer;
	color: #7d7d7d;
	line-height: 0;

	&:focus-visible {
		outline: 2px solid #6e0fea;
		outline-offset: 2px;
	}
`;

export interface ManageTaskSetsDrawerProps {
	isOpen: boolean;
	project: unknown;
	/** Opens straight on this set's config pane. The chip supplies it. */
	initialSetId?: string | null;
	/**
	 * Bumped by the caller on every open request. Without it, clicking the SAME
	 * chip after going Back changes no prop, fires no effect, and leaves the drawer
	 * sitting on the list. Same idiom as the create drawer's `drawerOpenId`
	 * (canvas_projects_scheduling.js:797).
	 */
	openNonce?: number;
	onClose: () => void;
}

const drawerFragment = graphql`
	fragment ManageTaskSetsDrawer_project on ProjectType {
		taskSets(first: 1000) {
			edges {
				node {
					id
					name
					frequency
					intervalLength
					weekdays
					monthlyWeekday
					monthlyMode
					dayOfMonth
					weekOrdinal
					boundType
					boundCount
					boundEndDate
					materialisedTo
					seedDate
					instanceCount
					exceptionCount
					parentPhase {
						id
						name
					}
				}
			}
		}
	}
`;

/**
 * The task-set surface, mounted identically by the scoping page and the project
 * timeline.
 *
 * One TaskSetDrawer whose body swaps between the set list and one set's config.
 * Three entry points -- the Manage task sets button on either page, and the set
 * chip on either page -- all set this one piece of state, so "one surface reached
 * several ways" is literally true rather than a claim about two implementations
 * that happen to match.
 *
 * Inactive panes are HIDDEN, never unmounted. The create drawer loses typed values
 * on a tab switch because its inactive tab panels unmount, and PIKA-744 will put
 * an edit form in the config pane here. Hiding rather than unmounting is what makes
 * that form survive a trip to the list and back, and it costs nothing today.
 */
export const ManageTaskSetsDrawer = ({
	isOpen,
	project,
	initialSetId = null,
	openNonce = 0,
	onClose,
}: ManageTaskSetsDrawerProps) => {
	const intl = useIntl();
	const data = useFragment(drawerFragment, project as never) as {
		taskSets?: {edges?: ReadonlyArray<{node?: TaskSetRow | null} | null> | null} | null;
	} | null;

	const sets: TaskSetRow[] = (data?.taskSets?.edges || [])
		.map(edge => edge?.node)
		.filter((node): node is TaskSetRow => !!node);

	// Which pane is showing.
	const [openSetId, setOpenSetId] = useState<string | null>(initialSetId);
	// The set the config pane is BUILT from. It deliberately outlives openSetId:
	// going Back hides the config pane, it does not destroy it. Cleared only when
	// the drawer closes.
	const [mountedSetId, setMountedSetId] = useState<string | null>(initialSetId);

	const showSet = (setId: string) => {
		setOpenSetId(setId);
		setMountedSetId(setId);
	};

	const showList = () => setOpenSetId(null);

	// Every open request wins over the pane the user last chose, INCLUDING a request
	// for the list (initialSetId null, which is what the toolbar button sends).
	//
	// Guarding this on `if (initialSetId)` would leave the button dead whenever the
	// config pane happened to be showing -- and that is reachable by design, not an
	// edge case: the drawer is deliberately non-blocking and the page beside it
	// stays live, so pressing Manage task sets while reading a set is an ordinary
	// thing to do.
	//
	// Keyed on the nonce as well as the id, because chip A -> Back -> chip A again
	// changes no id and would otherwise fire nothing. `mountedSetId` is only
	// advanced for a real set, so asking for the list hides the config pane without
	// destroying it.
	useEffect(() => {
		if (initialSetId) {
			showSet(initialSetId);
		} else {
			showList();
		}
		// Deps are deliberately partial: showSet/showList are recreated every render
		// and listing them would re-run this on each one. No disable directive here --
		// web-front does not enable the react-hooks plugin (.eslintrc.js lists no
		// react-hooks), so naming one of its rules in a disable comment is itself an
		// eslint error: "Definition for rule ... was not found".
	}, [initialSetId, openNonce]);

	// Deliberately NO reset on close. TaskSetDrawer slides out over 250ms
	// (visibility 0s linear 250ms), so clearing openSetId here swapped the title
	// from 'About this task set' back to 'Manage task sets' and tore off the back
	// button and name chip while the drawer was still on screen -- the user watched
	// it revert to the list as it left. TimelineManageTaskSetsDrawer keeps itself
	// mounted via hasOpened for precisely this animation; discarding the pane
	// underneath it defeated that.
	//
	// Nothing needs the reset: every open request bumps openNonce
	// (ProjectScopingPage.js:507, :1061; canvas_projects_scheduling.js:822), so the
	// effect above re-runs on each open and selects the pane afresh. Both callers
	// therefore leave setId alone on close -- nulling it there would re-fire that
	// effect and cause the same mid-slide flip by another route.

	const openSet = sets.find(set => set.id === openSetId) || null;
	const mountedSet = sets.find(set => set.id === mountedSetId) || null;

	return (
		<TaskSetDrawer
			isOpen={isOpen}
			title={openSet ? intl.formatMessage({id: 'task_set.about'}) : intl.formatMessage({id: 'task_set.manage'})}
			titleSize={openSet ? 17 : 20}
			titleAdornment={
				openSet ? (
					<>
						<BackButton
							type="button"
							onClick={showList}
							aria-label={intl.formatMessage({id: 'common.back'})}
							data-cy="task-set-drawer-back"
						>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
								<path d="M12 5l-5 5 5 5" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</BackButton>
						<TaskSetNameChip set={openSet} />
					</>
				) : null
			}
			onClose={onClose}
		>
			<div hidden={!!openSet} data-cy="task-set-list-pane">
				<TaskSetListPane sets={sets} onViewSet={showSet} />
			</div>
			{/* Back is a pane switch, not a state reset -- it never touches mountedSetId.
			    Keyed on mountedSet, NOT openSet: once a set has been opened the pane
			    stays in the DOM and merely hides on Back, so PIKA-744's edit form keeps
			    what the user typed. Conditional rendering here would reproduce the exact
			    PIKA-722 fault this design exists to avoid. */}
			{mountedSet ? (
				<div hidden={!openSet} data-cy="task-set-config-pane">
					<TaskSetConfigPane set={mountedSet} />
				</div>
			) : null}
		</TaskSetDrawer>
	);
};
